import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes'
import { z } from 'zod'
import type { ConvertQuestionnaireToLinkIdAnswerType } from '#src/models/questionnaires/utils/ConvertQuestionnaireToLinkIdAnswerType.ts'
import { removeEmptyAnswersFromQuestionnaireResponse } from '#src/models/questionnaires/utils/removeEmptyAnswersFromQuestionnaireResponse.ts'
import { generateUrnUuid } from '#src/utils.ts'

export type QuestionnaireResponseWithTypedAnswers<
  TQuestionnaire extends Questionnaire,
  TOptions extends { partial?: boolean } = object,
> = {
  resource: QuestionnaireResponse
  answers: ConvertQuestionnaireToLinkIdAnswerType<TQuestionnaire, TOptions>
  internalReferenceFullUrl: string
}

/**
 * Takes a questionnaire response and a questionnaire definition, and validates the response
 * against the definition. If the response is valid, it returns the answers extracted from the response.
 * The answers are strongly typed based on the questionnaire definition.
 *
 * To get this to work, you must have a questionnaire definition typed as follows:
 *
 * ```
 * const questionnaireDefinition = { // don't use : Questionnaire here! breaks the type inference
 *   resourceType: 'Questionnaire',
 *   items: [
 *     // each item in the questionnaire
 *   ] as const, // <-- this is required to enables strong typing
 *   // other fields...
 * } satisfies Questionnaire // <-- also required to enable strong typing
 * ```
 *
 * TODO some types are not yet supported! To add support for a new type, you need to add support for each type-based switch
 * statement in this file and in ConvertQuestionnaireToLinkIdAnswerType.ts. Search for `type-based switch` to see
 * where to add support.
 *
 * @param questionnaireResponse - the response to extract answers from
 * @param questionnaireDefinition - the definition of the questionnaire
 * @returns an object with the answers extracted from the questionnaire response, strongly typed
 */
export function extractTypedAnswersFromQuestionnaireResponse<
  const TQuestionnaire extends Questionnaire,
  const TOptions extends { partial?: boolean },
>(
  rawQuestionnaireResponse: QuestionnaireResponse,
  questionnaireDefinition: TQuestionnaire,
  options?: TOptions
): QuestionnaireResponseWithTypedAnswers<TQuestionnaire, TOptions> {
  const cleanedQR = removeEmptyAnswersFromQuestionnaireResponse(
    rawQuestionnaireResponse
  )

  const zodSchema = constructZodSchemaFromQuestionnaireDefinition(
    questionnaireDefinition,
    options ?? {}
  )

  // response items have the same linkId as the definition items, but don't have fields like
  // type which indicate how to extract the raw answer from the response item. so,
  // we first construct a mapping of linkId to definition item, then use that to extract the answers
  const linkIdToDefinitionItem =
    getLinkIdToDefinitionItemFromQuestionnaireDefinition(
      questionnaireDefinition
    )

  const answers = extractAnswersFromQuestionnaireResponse(
    cleanedQR,
    linkIdToDefinitionItem
  )

  // @ts-expect-error since we are dynamically constructing the schema, we need to use the any type
  const parsedAnswers: ConvertQuestionnaireToLinkIdAnswerType<
    TQuestionnaire,
    TOptions
  > = zodSchema.parse(answers)

  return {
    resource: cleanedQR,
    answers: parsedAnswers,
    internalReferenceFullUrl: generateUrnUuid(),
  }
}

export function extractTypedAnswersFromQuestionnaireResponseSafe<
  const TQuestionnaire extends Questionnaire,
  const TOptions extends { partial?: boolean },
>(
  questionnaireResponse: QuestionnaireResponse | null,
  questionnaireDefinition: TQuestionnaire,
  options?: TOptions
):
  // returns [answers, null] if successful
  // returns [null, error] if unsuccessful
  | [QuestionnaireResponseWithTypedAnswers<TQuestionnaire, TOptions>, null]
  | [null, string] {
  if (questionnaireResponse === null) {
    return [null, 'QuestionnaireResponse is null']
  }

  try {
    return [
      extractTypedAnswersFromQuestionnaireResponse(
        questionnaireResponse,
        questionnaireDefinition,
        options
      ),
      null,
    ]
  } catch (e) {
    return [null, e instanceof Error ? e.message : String(e)]
  }
}

/**
 * =================
 * Zod Schema Construction Utilities
 * =================
 */

function constructZodSchemaFromQuestionnaireDefinition(
  questionnaireDefinition: Questionnaire,
  options: { partial?: boolean }
): z.AnyZodObject {
  return constructZodSchemaFromGroupItemDefinition(
    {
      linkId: 'topLevelLinkId',
      item: questionnaireDefinition.item,
      type: 'group',
    },
    options
  )
}

function constructZodSchemaFromQuestionnaireItemDefinition(
  item: QuestionnaireItem,
  options: { partial?: boolean }
): z.ZodTypeAny | null {
  // =================
  // type-based switch
  // =================
  let type: z.ZodTypeAny
  switch (item.type) {
    case 'string':
    case 'text':
      type = z.string()
      break
    case 'boolean':
      type = z.boolean()
      break
    case 'date':
      // see full regex at https://hl7.org/fhir/R4B/datatypes.html#date
      type = z
        .string()
        .regex(
          /^([0-9]([0-9]([0-9][1-9]|[1-9]0)|[1-9]00)|[1-9]000)(-(0[1-9]|1[0-2])(-(0[1-9]|[1-2][0-9]|3[0-1]))?)?/
        )
      break
    case 'integer':
      type = z.number()
      break
    case 'decimal':
      type = z.number()
      break
    case 'attachment':
      type = z.object({
        contentType: z.string().optional(),
        language: z.string().optional(),
        data: z.string().optional(),
        url: z.string().url().optional(),
        size: z.number().int().nonnegative().optional(),
        hash: z.string().optional(),
        title: z.string().optional(),
        creation: z.string().optional(),
      })
      break
    case 'reference':
      type = z.object({
        reference: z.string(),
        display: z.string().optional(),
      })
      break
    case 'display':
      return null
    case 'choice':
    case 'open-choice':
      if (item.answerValueSet !== undefined) {
        throw new Error(
          `AnswerValueSet is not yet supported for linkId ${item.linkId}`
        )
      }

      if (
        item.answerOption?.every((option) => option.valueCoding !== undefined)
      ) {
        // Handle valueCoding answers
        type = z
          .object({
            system: z.string().optional(),
            code: z.string(),
            display: z.string().optional(),
          })
          .optional()
      } else if (
        item.answerOption?.every((option) => option.valueString !== undefined)
      ) {
        // Handle string answers
        type = z.enum(
          item.answerOption.map((option) => String(option.valueString)) as [
            string,
            ...string[],
          ]
        )
      } else {
        type = z.string()
      }
      break
    case 'group':
      type = constructZodSchemaFromGroupItemDefinition(item, options)
      break

    default:
      throw new Error(`Type ${item.type} is not yet supported`)
  }

  if (item.repeats === true) {
    type = z.array(type)
  }

  if (item.required !== true || options?.partial) {
    type = type.optional()
  }

  return type
}

function constructZodSchemaFromGroupItemDefinition(
  item: QuestionnaireItem,
  options: { partial?: boolean }
): z.AnyZodObject {
  const zodRawSchema: Record<string, z.ZodTypeAny> = {}
  item.item?.forEach((subItem) => {
    const zodType = constructZodSchemaFromQuestionnaireItemDefinition(
      subItem,
      options
    )
    if (zodType === null) {
      // skip types that don't have a zod representation
      return
    }
    zodRawSchema[subItem.linkId] = zodType
  })
  return z.object(zodRawSchema)
}
/**
 * =================
 * Answer Extraction Utilities
 * =================
 */

function extractAnswersFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse,
  linkIdToDefinitionItem: LinkIdToDefinitionItem
): Record<string, ValidAnswerTypes> {
  return extractAnswerFromGroupResponseItem(
    {
      linkId: 'topLevelLinkId',
      item: questionnaireResponse.item,
    },
    linkIdToDefinitionItem
  )
}

function extractAnswerFromResponseItemRecursively(
  responseItem: QuestionnaireResponseItem,
  linkIdToDefinitionItem: LinkIdToDefinitionItem
): Record<string, ValidAnswerTypes> | ValidAnswerTypes | undefined {
  const definitionItem = linkIdToDefinitionItem.get(responseItem.linkId)
  if (definitionItem === undefined) {
    // this is a response item that doesn't have a corresponding definition item, meaning it's not part of the questionnaire
    // we can safely ignore it
    return undefined
  }

  if (definitionItem.type === 'group') {
    return extractAnswerFromGroupResponseItem(
      responseItem,
      linkIdToDefinitionItem
    )
  } else {
    return extractAnswerFromNonGroupResponseItem(responseItem, definitionItem)
  }
}

interface GroupAnswers {
  [linkId: string]: ValidAnswerTypes
}

function extractAnswerFromGroupResponseItem(
  responseItem: QuestionnaireResponseItem,
  linkIdToDefinitionItem: LinkIdToDefinitionItem
): GroupAnswers {
  const gatheredSubAnswers: GroupAnswers = {}
  responseItem.item?.forEach((subItem) => {
    const subAnswer = extractAnswerFromResponseItemRecursively(
      subItem,
      linkIdToDefinitionItem
    )
    if (
      subAnswer === undefined ||
      (Array.isArray(subAnswer) && subAnswer.length === 0)
    ) {
      return
    }

    const subDefinitionItem = linkIdToDefinitionItem.get(subItem.linkId)
    if (subDefinitionItem === undefined) {
      throw new Error(`No definition item found for linkId ${subItem.linkId}`)
    }

    // repeats can technically yield multiple answers in two ways:
    // 1. the answer is an array of length > 1
    // 2. two separate response items have the same linkId.
    // assuming here for simplicity and based on empirical testing of Medplum's QuestionnaireForm behavior
    // that only groups can have separate response items with the same linkId, while
    // non-group items will have a single response item with an array of answers.
    // See https://hl7.org/fhir/R4B/questionnaire-definitions.html#Questionnaire.item.repeats

    if (
      subDefinitionItem.type === 'group' &&
      subDefinitionItem.repeats === true
    ) {
      // if it repeats, we return an array of the subAnswers
      const subAnswersForLinkId =
        (gatheredSubAnswers[subItem.linkId] as ValidAnswerType[]) ?? []
      // the answer itself shouldn't be an array
      subAnswersForLinkId.push(subAnswer as ValidAnswerType)
      gatheredSubAnswers[subItem.linkId] = subAnswersForLinkId
    } else {
      if (gatheredSubAnswers[subItem.linkId] !== undefined) {
        throw new Error(
          `Multiple response items for linkId ${subItem.linkId} of type ${subDefinitionItem.type}; only group items with repeats can have multiple response items`
        )
      }

      gatheredSubAnswers[subItem.linkId] = subAnswer
    }
  })
  return gatheredSubAnswers
}

function extractAnswerFromNonGroupResponseItem(
  responseItem: QuestionnaireResponseItem,
  definitionItem: QuestionnaireItem
): ValidAnswerTypes | undefined {
  // get the key for the answer object
  const answerKey = getAnswerKeyFromDefinitionItem(definitionItem)
  if (answerKey === null) {
    return undefined
  }

  // eg; answer [{valueString: 'some answer'}] => ['some answer']
  const answerArr = responseItem.answer?.map((answer) => answer[answerKey])

  if (definitionItem.repeats === true) {
    // if it repeats, we return an array
    return answerArr
  } else {
    if (answerArr !== undefined && answerArr.length > 1) {
      throw new Error(
        `Multiple answers found for non-repeating linkId ${responseItem.linkId}`
      )
    }

    // if it doesn't repeat, we just return the first element
    return answerArr?.[0]
  }
}

type ValidAnswerKeys = Exclude<
  keyof QuestionnaireResponseItemAnswer,
  'id' | 'extension' | 'item' | 'modifierExtension'
>
/**
 * A valid answer type is one of the value[x] keys in the QuestionnaireResponseItemAnswer type.
 */
type ValidAnswerType = QuestionnaireResponseItemAnswer[ValidAnswerKeys]

/**
 * Answer types can be either an array of answers or a single answer.
 */
type ValidAnswerTypes = ValidAnswerType | ValidAnswerType[]

function getAnswerKeyFromDefinitionItem(
  definitionItem: QuestionnaireItem
): ValidAnswerKeys | null {
  // =================
  // type-based switch
  // =================
  switch (definitionItem.type) {
    case 'string':
    case 'text':
      return 'valueString'
    case 'boolean':
      return 'valueBoolean'
    case 'date':
      return 'valueDate'
    case 'integer':
      return 'valueInteger'
    case 'decimal':
      return 'valueDecimal'
    case 'attachment':
      return 'valueAttachment'
    case 'reference':
      return 'valueReference'
    case 'display':
      // some items don't have answers
      return null
    case 'choice':
    case 'open-choice':
      // If the definition has answerOption with valueCoding, use valueCoding
      if (
        definitionItem.answerOption?.every(
          (option) => option.valueCoding !== undefined
        )
      ) {
        return 'valueCoding'
      }
      // Otherwise default to valueString
      return 'valueString'
    default:
      throw new Error(`Type ${definitionItem.type} is not yet supported`)
  }
}

/**
 * =================
 * LinkIdToDefinitionItem Mapping Utilities
 * =================
 */
type LinkIdToDefinitionItem = Map<string, QuestionnaireItem>

function getLinkIdToDefinitionItemFromQuestionnaireDefinition(
  questionnaireDefinition: Questionnaire
): LinkIdToDefinitionItem {
  const linkIdToDefinitionItem: LinkIdToDefinitionItem = new Map()
  questionnaireDefinition.item?.forEach((item) => {
    mapLinkIdToDefinitionItemRecursively(item, linkIdToDefinitionItem)
  })
  return linkIdToDefinitionItem
}

function mapLinkIdToDefinitionItemRecursively(
  item: QuestionnaireItem,
  linkIdToDefinitionItem: LinkIdToDefinitionItem
): void {
  linkIdToDefinitionItem.set(item.linkId, item)
  item.item?.forEach((subItem) => {
    mapLinkIdToDefinitionItemRecursively(subItem, linkIdToDefinitionItem)
  })
}
